// apps/client_diy_webapp/src/pages/qualitative-surveys/components/ResearchActivity.js
import React, { useEffect, useState } from "react";
import { RiFileTextLine, RiLinkM, RiPencilLine } from "react-icons/ri";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import RadioCard from "./RadioCard";
import MultiSelectChip from "./MultiSelectChip";
import SaveBar from "./Savebar";
import { useProjectUpdate } from "../useProjectUpdate";
import { SELECT_STYLE } from "../../../Utlils";
import { EDITABLE_STATUSES } from "../qualitativeUtils";



const SESSION_METHODS = [
    { value: "remote_online", title: "Remote — Online", description: "Participants will join through an online meeting platform." },
    { value: "remote_phone", title: "Remote — Over the Phone", description: "Participants will complete the session over a phone call." },
    { value: "in_person", title: "In-person", description: "Participants will attend the session at a physical location." },
];

const MEETING_PLATFORM_OPTIONS = [
    { value: "zoom", label: "Zoom" },
    { value: "google_meet", label: "Google Meet" },
    { value: "microsoft_teams", label: "Microsoft Teams" },
    { value: "client_provided_link", label: "Client-provided link" },
    { value: "mirats_to_arrange", label: "Mirats to arrange" },
    { value: "other", label: "Other" },
];

const PHONE_CALL_OPTIONS = [
    { value: "mirats_calls_participant", label: "Mirats will call the participant" },
    { value: "client_calls_participant", label: "Client will call the participant" },
    { value: "participant_calls_number", label: "Participant will call the provided number" },
    { value: "to_be_decided", label: "To be decided later" },
];

const DEVICE_OPTIONS = [
    { value: "computer_with_webcam", label: "Computer with webcam" },
    { value: "computer_without_webcam", label: "Computer without webcam" },
    { value: "smartphone", label: "Smartphone" },
    { value: "tablet", label: "Tablet" },
    { value: "any_device", label: "Any device" },
    { value: "no_specific_device", label: "No specific device" },
];

const BROWSER_OPTIONS = [
    { value: "any_browser", label: "Any browser" },
    { value: "google_chrome", label: "Google Chrome" },
    { value: "safari", label: "Apple Safari" },
    { value: "microsoft_edge", label: "Microsoft Edge" },
    { value: "firefox", label: "Mozilla Firefox" },
    { value: "other", label: "Other" },
];

const OS_OPTIONS = [
    { value: "any_os", label: "Any operating system" },
    { value: "windows", label: "Windows" },
    { value: "macos", label: "macOS" },
    { value: "linux", label: "Linux" },
    { value: "other", label: "Other" },
];

const RECORDING_OPTIONS = [
    { value: "yes", label: "Yes, notify participants that the session will be recorded" },
    { value: "no", label: "No, the session will not be recorded" },
    { value: "not_sure", label: "Not sure yet" },
];




export default function ResearchActivity({ projectDetails }) {
    const { user } = useGlobalContext();
    const organizationID = user?.organization?._id;
    const projectID = projectDetails?._id;
    const isEditable = EDITABLE_STATUSES.includes(projectDetails?.status);

    // ── Research Activity fields ─────────────────────────────────────────────
    const [mode, setMode] = useState(projectDetails?.researchActivity?.mode ?? "moderated");
    const [sessionType, setSessionType] = useState(projectDetails?.researchActivity?.moderatedSessionType ?? "");
    const [sessionMethod, setSessionMethod] = useState(projectDetails?.researchActivity?.sessionMethod ?? "");
    const [meetingPlatform, setMeetingPlatform] = useState(projectDetails?.researchActivity?.meetingPlatform ?? "");
    const [meetingPlatformOther, setMeetingPlatformOther] = useState(projectDetails?.researchActivity?.meetingPlatformOther ?? "");
    const [phoneCallType, setPhoneCallType] = useState(projectDetails?.researchActivity?.phoneCallType ?? "");
    const [sessionLocation, setSessionLocation] = useState(projectDetails?.researchActivity?.sessionLocation ?? "");

    // ── Requirements fields ──────────────────────────────────────────────────
    const [preparationInstructions, setPreparationInstructions] = useState(
        projectDetails?.requirements?.preparationInstructions ?? ""
    );
    // deviceTypes is an array
    const [deviceTypes, setDeviceTypes] = useState(
        projectDetails?.requirements?.deviceTypes ?? ["computer_with_webcam"]
    );
    // webBrowser is a single string — wrap in array for MultiSelectChip, unwrap on save
    const [webBrowser, setWebBrowser] = useState(
        projectDetails?.requirements?.webBrowser
            ? [projectDetails.requirements.webBrowser]
            : ["any_browser"]
    );
    // operatingSystem is a single string — same pattern
    const [operatingSystem, setOperatingSystem] = useState(
        projectDetails?.requirements?.operatingSystem
            ? [projectDetails.requirements.operatingSystem]
            : ["any_os"]
    );
    const [recording, setRecording] = useState(
        projectDetails?.requirements?.sessionRecordingNotification ?? ""
    );

    // Sync on prop change
    useEffect(() => {
        setMode(projectDetails?.researchActivity?.mode ?? "moderated");
        setSessionType(projectDetails?.researchActivity?.moderatedSessionType ?? "");
        setSessionMethod(projectDetails?.researchActivity?.sessionMethod ?? "");
        setMeetingPlatform(projectDetails?.researchActivity?.meetingPlatform ?? "");
        setMeetingPlatformOther(projectDetails?.researchActivity?.meetingPlatformOther ?? "");
        setPhoneCallType(projectDetails?.researchActivity?.phoneCallType ?? "");
        setSessionLocation(projectDetails?.researchActivity?.sessionLocation ?? "");
        setPreparationInstructions(projectDetails?.requirements?.preparationInstructions ?? "");
        setDeviceTypes(projectDetails?.requirements?.deviceTypes ?? ["computer_with_webcam"]);
        setWebBrowser(projectDetails?.requirements?.webBrowser || ["any_browser"]);
        setOperatingSystem(projectDetails?.requirements?.operatingSystem || ["any_os"]);
        setRecording(projectDetails?.requirements?.sessionRecordingNotification ?? "");
    }, [projectDetails]);

    const { save, isSaving, saveError, saveSuccess } = useProjectUpdate(organizationID, projectID);

    const handleSave = () =>
        save({
            researchActivity: {
                mode,
                moderatedSessionType: sessionType,
                sessionMethod,
                meetingPlatform: meetingPlatform || null,
                meetingPlatformOther: meetingPlatformOther || null,
                phoneCallType: phoneCallType || null,
                sessionLocation: sessionLocation || null,
            },
            requirements: {
                preparationInstructions,
                deviceTypes,
                webBrowser: webBrowser ?? ["any_browser"],
                operatingSystem: operatingSystem ?? ["any_os"],
                sessionRecordingNotification: recording || null,
            },
        });

    return (
        <div className="space-y-6">
            {/* ── Research Mode ────────────────────────────────────────── */}
            <div className="space-y-3">
                <div>
                    <h1 className="font-bold text-md">Type of Research Activity</h1>
                    <p className="text-[#667085] text-xs">Select the research format for this project.</p>
                </div>
                <RadioCard
                    title="Moderated"
                    description="Participants are guided by a moderator during the session."
                    selected={mode === "moderated"}
                    onClick={() => isEditable && setMode("moderated")}
                    disabled={!isEditable}
                />
                <div className="space-y-2 cursor-not-allowed">
                    <RadioCard title="Unmoderated" description="Participants complete the research activity independently." disabled chipText={"Coming Soon"} />
                </div>
                <div className="space-y-2 cursor-not-allowed">
                    <RadioCard title="Multi-day" description="Participants complete tasks or diary activities over multiple days." disabled chipText={"Coming Soon"} />
                </div>
            </div>

            {/* ── IDI / FGD ────────────────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-md">
                <div>
                    <h1 className="font-bold text-md">How do you want to conduct this moderated research?</h1>
                    <p className="text-[#667085] text-xs">Select the number of participants per session.</p>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <RadioCard
                        title="One participant per session"
                        description="Best for 1-on-1 interviews, expert interviews, product feedback, usability sessions, or deep conversations."
                        selected={sessionType === "idi"}
                        onClick={() => isEditable && setSessionType("idi")}
                        chipText="IDI"
                        disabled={!isEditable}
                    />
                    <RadioCard
                        title="Multiple participants per session"
                        description="Best for focus groups, group discussions, concept testing, co-creation, or group reactions."
                        selected={sessionType === "fgd"}
                        onClick={() => isEditable && setSessionType("fgd")}
                        chipText="FGD"
                        disabled={!isEditable}
                    />
                </div>
            </div>

            {/* ── Session Method ───────────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-md">
                <div>
                    <h1 className="font-bold text-md">How Will the Session Be Conducted?</h1>
                    <p className="text-[#667085] text-xs">Select the session delivery method.</p>
                </div>
                <div className="flex items-center justify-between gap-3">
                    {SESSION_METHODS.map((m) => (
                        <RadioCard
                            key={m.value}
                            title={m.title}
                            description={m.description}
                            selected={sessionMethod === m.value}
                            onClick={() => {
                                if (!isEditable) return;
                                setSessionMethod(m.value);
                                // clear conditional fields on method change
                                setMeetingPlatform("");
                                setMeetingPlatformOther("");
                                setPhoneCallType("");
                                setSessionLocation("");
                            }}
                            disabled={!isEditable}
                        />
                    ))}
                </div>

                {/* Remote Online → meeting platform */}
                {sessionMethod === "remote_online" && (
                    <div className="border border-gray-100 bg-gray-50 p-3 rounded-xl space-y-3">
                        <div className="space-y-1.5">
                            <p className="text-gray-600 text-sm font-medium">
                                Meeting Platform <span className="text-red-500">*</span>
                            </p>
                            <select
                                className="border border-gray-300 rounded-md py-2.5 px-[14px] text-[#667085] text-[14px]
                                    focus:outline-none focus:ring-1 focus:ring-[#0226BE] transition-all appearance-none
                                    cursor-pointer hover:border-gray-400 bg-white w-full disabled:bg-gray-50 disabled:cursor-not-allowed"
                                style={SELECT_STYLE}
                                value={meetingPlatform ?? ""}
                                onChange={(e) => isEditable && setMeetingPlatform(e.target.value)}
                                disabled={!isEditable}
                            >
                                <option value="">Select the meeting platform</option>
                                {MEETING_PLATFORM_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        {meetingPlatform === "other" && (
                            <div className="space-y-1.5">
                                <p className="text-gray-600 text-sm font-medium">
                                    Please specify <span className="text-red-500">*</span>
                                </p>
                                <div className="border rounded-lg px-[14px] py-2.5 flex items-center gap-2 bg-white">
                                    <input
                                        type="text"
                                        className="w-full text-sm placeholder:text-sm focus:outline-none bg-transparent"
                                        placeholder="e.g. Webex, BlueJeans…"
                                        value={meetingPlatformOther}
                                        onChange={(e) => isEditable && setMeetingPlatformOther(e.target.value)}
                                        disabled={!isEditable}
                                        maxLength={100}
                                    />
                                    <RiPencilLine size={16} className="text-gray-300 flex-shrink-0" />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Remote Phone → call arrangement */}
                {sessionMethod === "remote_phone" && (
                    <div className="border border-gray-100 bg-gray-50 p-3 rounded-xl">
                        <p className="text-gray-600 text-sm font-medium mb-1.5">
                            Phone Call Arrangement <span className="text-red-500">*</span>
                        </p>
                        <select
                            className="border border-gray-300 rounded-md py-2.5 px-[14px] text-[#667085] text-[14px]
                                focus:outline-none focus:ring-1 focus:ring-[#0226BE] transition-all appearance-none
                                cursor-pointer hover:border-gray-400 bg-white w-full disabled:bg-gray-50 disabled:cursor-not-allowed"
                            style={SELECT_STYLE}
                            value={phoneCallType ?? ""}
                            onChange={(e) => isEditable && setPhoneCallType(e.target.value)}
                            disabled={!isEditable}
                        >
                            <option value="">Select arrangement</option>
                            {PHONE_CALL_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* In-person → location */}
                {sessionMethod === "in_person" && (
                    <div className="border border-gray-100 bg-gray-50 p-3 rounded-xl">
                        <p className="text-gray-600 text-sm font-medium mb-1.5">
                            Session Location <span className="text-red-500">*</span>
                        </p>
                        <textarea
                            className="w-full border border-gray-300 rounded-md px-[14px] py-[10px] text-sm
                                placeholder:text-sm focus:outline-none focus:border-[#0226BE] resize-none
                                transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                            placeholder="Enter city, venue, address, or location instructions."
                            rows={3}
                            maxLength={1000}
                            value={sessionLocation}
                            onChange={(e) => isEditable && setSessionLocation(e.target.value)}
                            disabled={!isEditable}
                        />
                    </div>
                )}
            </div>

            {/* ── Preparation Instructions ──────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-md">
                <div className="space-y-1">
                    <h1 className="font-bold text-md">Preparation Instructions</h1>
                    <p className="text-gray-500 font-regular text-xs">
                        Help participants be fully prepared by providing clear instructions for what they
                        need to do or have on hand before the session starts.
                    </p>
                </div>
                <div className="border rounded-lg px-[14px] py-2.5 flex items-start gap-2 bg-white">
                    <textarea
                        className="w-full text-sm placeholder:text-sm resize-none focus:outline-none bg-transparent
                            disabled:text-gray-500"
                        placeholder="Example: Please join from a quiet place with a stable internet connection.
Keep your smartphone nearby, as we may ask you to show how you use certain apps."
                        rows={4}
                        maxLength={2000}
                        value={preparationInstructions}
                        onChange={(e) => isEditable && setPreparationInstructions(e.target.value)}
                        disabled={!isEditable}
                    />
                    <RiPencilLine size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                </div>
                <div className="flex justify-between">
                    <p className="text-gray-400 text-xs">Optional</p>
                    <p className="text-gray-400 text-xs">{preparationInstructions.length}/2000</p>
                </div>
            </div>

            {/* ── Device Requirements ───────────────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-md">
                <h1 className="font-bold text-md">Device Requirements</h1>
                <div className="space-y-3">
                    {/* deviceTypes is array — correct */}
                    <MultiSelectChip
                        label="Device Type"
                        required
                        placeholder="Select device types required"
                        options={DEVICE_OPTIONS}
                        selected={deviceTypes}
                        onChange={(val) => isEditable && setDeviceTypes(val)}
                        name="device_type"
                    />
                    {/* webBrowser is string in DB — wrapped as single-item array for chip UI */}
                    <MultiSelectChip
                        label="Web Browser"
                        placeholder="Select browser requirement"
                        options={BROWSER_OPTIONS}
                        selected={webBrowser}
                        onChange={(val) => {
                            if (!isEditable) return;
                            // only allow 1 value — replace rather than append
                            // setWebBrowser(val.length > 0 ? [val[val.length - 1]] : ["any_browser"]);
                            setWebBrowser(val);
                        }}
                        name="web_browser"
                    />
                    {/* operatingSystem is string in DB — same pattern */}
                    <MultiSelectChip
                        label="Operating System"
                        placeholder="Select OS requirement"
                        options={OS_OPTIONS}
                        selected={operatingSystem}
                        onChange={(val) => {
                            if (!isEditable) return;
                            // only allow 1 value — replace rather than append
                            // setOperatingSystem(val.length > 0 ? [val[val.length - 1]] : ["any_os"]);
                            setOperatingSystem(val);
                        }}
                        name="operating_system"
                    />
                </div>
            </div>

            {/* ── Session Recording Notification ────────────────────────── */}
            <div className="space-y-3 bg-white p-3 rounded-md">
                <div>
                    <h1 className="font-bold text-md">Session Recording Notification</h1>
                    <p className="text-[#667085] text-xs">
                        Selecting this option only notifies participants that the session may be recorded.
                        Mirats Quanto DIY will not record the session unless recording tools are separately enabled.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <p className="text-gray-600 text-sm font-medium">
                        Will participants be notified that the session may be recorded?
                        <span className="text-red-500">*</span>
                    </p>
                    <select
                        className="border border-gray-300 rounded-md py-2.5 px-[14px] text-[#667085] text-[14px]
                            focus:outline-none focus:ring-1 focus:ring-[#0226BE] transition-all appearance-none
                            cursor-pointer hover:border-gray-400 bg-white w-full disabled:bg-gray-50 disabled:cursor-not-allowed"
                        style={SELECT_STYLE}
                        value={recording ?? ""}
                        onChange={(e) => isEditable && setRecording(e.target.value)}
                        disabled={!isEditable}
                        name="sessionRecordingNotification"
                    >
                        <option value="">Select an option</option>
                        {RECORDING_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ── Save bar ──────────────────────────────────────────────── */}
            {isEditable && (
                <SaveBar
                    onSave={handleSave}
                    isSaving={isSaving}
                    saveSuccess={saveSuccess}
                    saveError={saveError}
                />
            )}
        </div>
    );
}